import { lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Send, CheckCircle, ArrowLeft, FileText, Sparkles, Lock, Zap, LogIn, UserPlus, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { SEOHead } from '@/components/SEOHead';

const CircuitBoard = lazy(() => import('@/components/effects/CircuitBoard').then(m => ({ default: m.CircuitBoard })));
const FloatingParticles = lazy(() => import('@/components/effects/FloatingParticles').then(m => ({ default: m.FloatingParticles })));

const PricingSection = lazy(() => import('@/components/pricing/PricingSection').then(m => ({ default: m.PricingSection })));

export default function Index() {
  const { user } = useAuth();

  return (
    <main className="min-h-screen bg-background">
      <SEOHead
        title="Gelovo Sign - חתימה דיגיטלית חכמה ומאובטחת"
        description="מערכת מתקדמת לחתימה דיגיטלית על מסמכי PDF. פשוט, מהיר ומאובטח."
        ogImage="/og-image.png"
        path="/"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Circuit board animation */}
        <Suspense fallback={null}><CircuitBoard /></Suspense>
        {/* Floating particles */}
        <Suspense fallback={null}><FloatingParticles count={35} /></Suspense>
        {/* Tech grid background */}
        <div className="absolute inset-0 tech-grid" />
        <div className="absolute inset-0 gradient-hero opacity-80" />
        
        {/* Glow effects */}
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full opacity-15" 
             style={{ background: 'radial-gradient(ellipse, hsl(174 72% 50% / 0.35) 0%, transparent 65%)' }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[300px] rounded-full opacity-10" 
             style={{ background: 'radial-gradient(circle, hsl(174 72% 50% / 0.25) 0%, transparent 70%)' }} />
        <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full opacity-10" 
             style={{ background: 'radial-gradient(circle, hsl(185 80% 45% / 0.2) 0%, transparent 70%)' }} />

        <div className="relative z-10">
          {/* Nav */}
          <nav className="container flex h-20 items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gradient-brand tracking-tight">Gelovo</span>
              <span className="text-2xl font-bold text-foreground tracking-tight">Sign</span>
            </div>
            <div className="flex items-center gap-3">
              {user ? (
                <>
                  <Link to="/dashboard">
                    <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                      דשבורד
                    </Button>
                  </Link>
                  <Link to="/documents">
                    <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                      המסמכים שלי
                    </Button>
                  </Link>
                  <a href="#pricing">
                    <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                      חבילות
                    </Button>
                  </a>
                   <Link to="/contact">
                     <Button className="btn-gelovo gap-2">
                       <Mail className="h-4 w-4" />
                       צור קשר
                     </Button>
                   </Link>
                </>
              ) : (
                <>
                  <a href="#pricing">
                    <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                      חבילות
                    </Button>
                  </a>
                   <Link to="/contact">
                     <Button variant="ghost" className="text-foreground/70 hover:text-foreground">
                       צור קשר
                     </Button>
                   </Link>
                  <Link to="/auth">
                    <Button variant="ghost" className="text-foreground/70 hover:text-foreground gap-1.5">
                      <LogIn className="h-4 w-4" />
                      התחברות
                    </Button>
                  </Link>
                  <Link to="/auth">
                    <Button className="btn-gelovo gap-1.5">
                      <UserPlus className="h-4 w-4" />
                      הרשמה
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </nav>

          {/* Hero content */}
          <div className="container py-24 pb-32">
            <div className="max-w-3xl mx-auto text-center animate-slide-up">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 mb-8">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm text-primary font-medium">פלטפורמת חתימה דיגיטלית מתקדמת</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight">
                <span className="text-foreground">חתימה דיגיטלית</span>
                <br />
                <span className="text-gradient">מאובטחת וחכמה</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-xl mx-auto">
                מערכת מתקדמת לחתימה דיגיטלית על מסמכי PDF. 
                פשוט, מהיר ומאובטח.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {user ? (
                  <>
                    <Link to="/documents">
                      <Button size="lg" className="btn-gelovo gap-2 text-lg">
                        המסמכים שלי
                        <ArrowLeft className="h-5 w-5" />
                      </Button>
                    </Link>
                    <Link to="/dashboard">
                      <Button size="lg" variant="outline" className="text-lg px-8 border-border/50 hover:bg-secondary">
                        דשבורד
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/auth">
                      <Button size="lg" className="btn-gelovo gap-2 text-lg">
                        <UserPlus className="h-5 w-5" />
                        הרשמה חינם
                      </Button>
                    </Link>
                    <Link to="/auth">
                      <Button size="lg" variant="outline" className="text-lg px-8 border-border/50 hover:bg-secondary gap-2">
                        <LogIn className="h-5 w-5" />
                        התחברות
                      </Button>
                    </Link>
                  </>
                )}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 relative overflow-hidden">
        <Suspense fallback={null}><FloatingParticles count={15} /></Suspense>
        <div className="container relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-foreground">
            איך זה עובד?
          </h2>
          <p className="text-center text-muted-foreground mb-16 max-w-lg mx-auto">
            שלושה צעדים פשוטים לחתימה דיגיטלית מאובטחת
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-8 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-glow animate-fade-in group">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 mb-6 group-hover:shadow-glow transition-all">
                <FileText className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">העלה מסמך</h3>
              <p className="text-muted-foreground leading-relaxed">
                העלה קובץ PDF והגדר את מיקום שדות החתימה בקלות
              </p>
            </div>
            <div className="p-8 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-glow animate-fade-in group" style={{ animationDelay: '0.1s' }}>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 mb-6 group-hover:shadow-glow transition-all">
                <Send className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">שלח לחתימה</h3>
              <p className="text-muted-foreground leading-relaxed">
                שלח קישור מאובטח לחותם עם הודעה מותאמת אישית
              </p>
            </div>
            <div className="p-8 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-glow animate-fade-in group" style={{ animationDelay: '0.2s' }}>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-success/10 mb-6 group-hover:shadow-glow transition-all">
                <CheckCircle className="h-7 w-7 text-success" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">קבל את החתימה</h3>
              <p className="text-muted-foreground leading-relaxed">
                קבל התראה כשהמסמך נחתם והורד את הגרסה החתומה
              </p>
            </div>
            <div className="p-8 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-glow animate-fade-in group" style={{ animationDelay: '0.3s' }}>
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 mb-6 group-hover:shadow-glow transition-all">
                <Shield className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-foreground">אבטחה מתקדמת</h3>
              <p className="text-muted-foreground leading-relaxed">
                הצפנה מלאה, RLS מובנה וביצועים מהירים לאבטחת המסמכים שלך
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <Suspense fallback={<section id="pricing" className="py-24"><div className="container"><div className="text-center text-muted-foreground">טוען חבילות...</div></div></section>}>
        <PricingSection />
      </Suspense>

      {/* Contact Section */}
      <section id="contact" className="py-20">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4 text-foreground">צרו קשר</h2>
          <p className="text-muted-foreground mb-8 max-w-md mx-auto">
            רוצים לשמוע עוד? יש לכם שאלות? נשמח לעזור!
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-gelovo text-lg gap-2">
              <Mail className="h-5 w-5" />
              צרו קשר
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/30 py-8">
        <div className="container text-center text-muted-foreground">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-lg font-bold text-gradient-brand">Gelovo</span>
            <span className="text-lg font-bold text-foreground">Sign</span>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} Gelovo Sign. כל הזכויות שמורות.</p>
        </div>
      </footer>
    </main>
  );
}
